import React from 'react';
import { usePublicUserCount } from '../lib/usePublicUserCount';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Zap, Droplets, Tv, Receipt, Shield, Clock, Smartphone, Bell, Check,
  ChevronRight, Star, Play, ArrowRight, Home, Sparkles,
} from 'lucide-react';
import MarketingLayout from './marketing/MarketingLayout';
import { IMAGES } from '../lib/marketingImages';
import { useAuth } from '../contexts/AuthContext';

const services = [
  {
    icon: Zap,
    title: 'Electricity',
    description: 'Prepaid ESCOM units on your phone in seconds. Lights back on before the kettle cools.',
    photo: IMAGES.heroPower,
    accent: 'from-yellow-400 to-amber-500',
  },
  {
    icon: Droplets,
    title: 'Water',
    description: 'Pay LWB, BWB and NRWB without leaving home. No queues, no disconnection scare.',
    photo: IMAGES.water,
    accent: 'from-cyan-400 to-sky-500',
  },
  {
    icon: Tv,
    title: 'TV packages',
    description: 'Renew DStv or GOtv before kickoff. Keep the living room buzzing.',
    photo: IMAGES.tv,
    accent: 'from-blue-500 to-indigo-500',
  },
  {
    icon: Receipt,
    title: 'Tax & more',
    description: 'MRA payments and more household bills, rolling out in one familiar flow.',
    photo: IMAGES.phonePay,
    accent: 'from-[#226D64] to-[#99E1D9]',
  },
];

const steps = [
  { n: '01', title: 'Pick what you need', text: 'Electricity, water, or TV — tap the one that keeps your home running.' },
  { n: '02', title: 'Enter your meter', text: 'Save it once. Next time it is a single tap. We check the account for you.' },
  { n: '03', title: 'Pay & you are done', text: 'Tokens and receipts land on your phone. Most people are finished in under a minute.' },
];

const features = [
  { icon: Zap, title: 'Units in seconds', text: 'No waiting around after you pay. Tokens typically arrive almost immediately.' },
  { icon: Shield, title: 'Safe by design', text: 'PIN-protected payments, encrypted sessions, and receipts you can keep.' },
  { icon: Clock, title: 'Open when you are', text: 'Pay at 6am or midnight. The queue is your sofa.' },
  { icon: Smartphone, title: 'Saved meters', text: 'Your home, your mum, the shop — all one tap away.' },
  { icon: Bell, title: 'Expiry nudges', text: 'We remind you before DStv or GOtv goes dark.' },
  { icon: Home, title: 'Built for households', text: 'Plain language, Malawian providers, and help on WhatsApp.' },
];

const stories = [
  {
    name: 'Chisomo Banda',
    place: 'Area 49, Lilongwe',
    photo: IMAGES.portraitA,
    quote: 'The lights went out during supper. I bought units from the table and they were on before the kids finished eating.',
  },
  {
    name: 'Thandiwe Phiri',
    place: 'Ndirande, Blantyre',
    photo: IMAGES.portraitB,
    quote: 'I used to miss the DStv deadline every month. Now I get a reminder and renew in the time it takes to make tea.',
  },
  {
    name: 'Yamikani Mwale',
    place: 'Luwinga, Mzuzu',
    photo: IMAGES.portraitC,
    quote: 'Paying the water board used to mean a whole morning. SmartPay is just… done. That is the whole story.',
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userCount = usePublicUserCount();
  const start = () => navigate(user ? '/buy' : '/register');

  return (
    <MarketingLayout>
      <section className="relative min-h-[88vh] flex items-end overflow-hidden pb-28 sm:pb-32">
        <img
          src={IMAGES.heroHome}
          alt="A warmly lit living room"
          className="absolute inset-0 w-full h-full object-cover scale-105 marketing-kenburns"
        />
        <div className="absolute inset-0 marketing-hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16 sm:py-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#99E1D9]/20 text-[#99E1D9] text-xs font-semibold uppercase tracking-wider mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Malawi’s household bill app
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] mb-5">
              Keep the lights on — in seconds.
            </h1>
            <p className="text-lg sm:text-xl text-white/85 mb-8 max-w-xl">
              Buy electricity, pay water, renew DStv from the couch. No queues. No paperwork. Just a home that stays on.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              <button
                onClick={start}
                className="px-6 py-3.5 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 shadow-lg shadow-black/20 inline-flex items-center gap-2"
              >
                <Zap className="w-5 h-5" /> {user ? 'Pay a bill now' : 'Get started free'}
              </button>
              <a
                href="#how-it-works"
                className="px-6 py-3.5 bg-white/15 text-white border border-white/30 rounded-xl font-semibold hover:bg-white/25 inline-flex items-center gap-2 backdrop-blur-sm"
              >
                <Play className="w-5 h-5" /> See how it works
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-5 text-white/90 text-sm">
              {userCount != null && (
                <span>
                  {userCount.toLocaleString()} active {userCount === 1 ? 'user' : 'users'}
                </span>
              )}
              {userCount != null && <span className="hidden sm:inline text-white/50">·</span>}
              <span className="hidden sm:inline">Tokens in ~30 seconds</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="services" className="relative z-20 -mt-24 px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((service, index) => (
            <motion.button
              key={service.title}
              type="button"
              onClick={start}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="group text-left bg-card rounded-2xl overflow-hidden border border-border shadow-xl hover:-translate-y-1 hover:shadow-2xl transition-all cursor-pointer"
            >
              <div className="relative h-32 overflow-hidden bg-gradient-to-br from-[#226D64] to-[#99E1D9]">
                <img src={service.photo} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className={`absolute top-3 left-3 w-10 h-10 rounded-xl bg-gradient-to-br ${service.accent} flex items-center justify-center text-white shadow-lg`}>
                  <service.icon className="w-5 h-5" />
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-lg mb-1">{service.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{service.description}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                  Pay now <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={IMAGES.phonePay} alt="Paying a bill on a phone" className="rounded-3xl w-full h-[420px] object-cover shadow-2xl" />
            <div className="absolute -bottom-6 -right-2 sm:right-8 bg-primary text-primary-foreground rounded-2xl px-6 py-5 shadow-xl">
              <p className="text-3xl font-extrabold">30s</p>
              <p className="text-sm opacity-90">typical token time</p>
            </div>
          </div>
          <div>
            <p className="text-primary font-semibold uppercase tracking-wider text-sm mb-3">Made for real homes</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">Bills should feel easy — not like a chore.</h2>
            <p className="text-muted-foreground text-lg mb-6">
              SmartPay is for people who just want the fridge humming and the match on. We speak household, not boardroom.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                'Prepaid electricity without the kiosk wait',
                'Water bills paid before disconnection day',
                'TV packages renewed from the living room',
                'Receipts you can screenshot and keep',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate('/about')}
              className="inline-flex items-center gap-2 font-semibold text-primary hover:underline"
            >
              Our story <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#EDF7F6] dark:bg-[#99E1D9]/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-primary font-semibold uppercase tracking-wider text-sm mb-2">How it works</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold">Three taps. Home stays on.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step) => (
              <div key={step.n} className="bg-card border border-border rounded-2xl p-8 relative overflow-hidden">
                <span className="text-6xl font-extrabold text-primary/15 absolute top-4 right-4">{step.n}</span>
                <p className="text-primary font-bold mb-3">{step.n}</p>
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <p className="text-muted-foreground">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-3">Why households stick with us</h2>
            <p className="text-muted-foreground text-lg">Little details that make paying bills feel human.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="p-2">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#226D64] to-[#99E1D9] text-white flex items-center justify-center mb-4 shadow-lg shadow-[#99E1D9]/30">
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="font-bold text-lg mb-1">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-card/40">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold uppercase tracking-wider text-sm mb-2">Neighbour stories</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold">“It just works, from home.”</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {stories.map((story) => (
              <figure key={story.name} className="bg-card border border-border rounded-2xl p-6 shadow-sm">
                <div className="flex gap-1 text-yellow-400 mb-4">
                  {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                </div>
                <blockquote className="text-foreground/90 leading-relaxed mb-6">“{story.quote}”</blockquote>
                <figcaption className="flex items-center gap-3">
                  <img src={story.photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <p className="font-semibold">{story.name}</p>
                    <p className="text-xs text-muted-foreground">{story.place}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <img src={IMAGES.nightCity} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F2E2B]/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 py-20 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">Ready for a quieter bill day?</h2>
          <p className="text-white/80 text-lg mb-8">Create a free account and pay your first bill in under a minute.</p>
          <button
            onClick={start}
            className="px-8 py-4 bg-[#99E1D9] text-[#0F2E2B] rounded-xl font-bold text-lg hover:bg-white transition-colors inline-flex items-center gap-2"
          >
            {user ? 'Go to my bills' : 'Create free account'} <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default LandingPage;
